import { Link } from "react-router-dom";
import { Star, Lock, PlayCircle, Clock } from "lucide-react";
import { resolveImg } from "@/lib/api";
import { gaEvent } from "@/lib/analytics";

const heroFallback = "https://images.unsplash.com/photo-1618049049816-43a00d5b0c3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export function CourseCard({ course, owned }) {
  const img = resolveImg(course.thumbnail || course.cover_image) || heroFallback;
  const hasSale = course.sale_price != null && course.sale_price < course.price;
  return (
    <Link
      to={`/course/${course.slug || course.id}`}
      data-testid={`course-card-${course.id}`}
      onClick={() => {
        const p = hasSale ? course.sale_price : course.price;
        gaEvent("select_item", {
          currency: "CAD",
          items: [{ item_id: course.id, item_name: course.title, price: Number(p) || 0 }],
        });
      }}
      className="group block overflow-hidden rounded-lg border border-white/10 bg-zinc-900/50 hover:border-[#DCA846]/50 hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-video overflow-hidden bg-zinc-800">
        <img src={img} alt={course.title} loading="lazy"
          className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-2">
          {course.is_free && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">FREE</Badge>}
          {course.is_new && <Badge className="bg-[#DCA846]/20 text-[#DCA846] border-[#DCA846]/30">NEW</Badge>}
          {course.is_coming_soon && <Badge className="bg-zinc-800 text-white border-zinc-700">SOON</Badge>}
          {course.type === "video" && <Badge className="bg-black/60 text-white border-white/20">VIDEO</Badge>}
        </div>
        {!course.is_free && !owned && (
          <div className="absolute top-3 right-3 rounded-full bg-black/60 p-1.5 border border-white/10">
            <Lock className="h-3.5 w-3.5 text-zinc-300" />
          </div>
        )}
        {(owned || course.is_free) && (
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <PlayCircle className="h-12 w-12 text-white drop-shadow-lg" />
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1 text-[#DCA846] mb-2">
          <Star className="h-3.5 w-3.5 fill-[#DCA846]" />
          <span className="text-xs text-zinc-300">{Number(course.rating || 5).toFixed(1)}</span>
          {course.lesson_count != null && (
            <span className="ml-2 flex items-center gap-1 text-xs text-zinc-500">
              <Clock className="h-3 w-3" /> {course.lesson_count} lessons
            </span>
          )}
        </div>
        <h3 className="font-display text-lg font-semibold leading-tight text-white line-clamp-2">{course.title}</h3>
        {course.short_description && (
          <p className="mt-1 text-sm text-zinc-400 line-clamp-2">{course.short_description}</p>
        )}
        <div className="mt-4 flex items-center justify-between">
          {course.is_free ? (
            <span className="font-display text-lg text-emerald-400">Free</span>
          ) : (
            <div className="flex items-baseline gap-2">
              <span className="font-display text-lg text-white">${Number(hasSale ? course.sale_price : course.price).toFixed(0)}</span>
              {hasSale && <span className="text-sm text-zinc-500 line-through">${Number(course.price).toFixed(0)}</span>}
            </div>
          )}
          <span className="text-xs uppercase tracking-wider text-[#DCA846] group-hover:translate-x-1 transition-transform">
            {owned ? "Continue →" : course.is_free ? "Watch →" : "View →"}
          </span>
        </div>
      </div>
    </Link>
  );
}

function Badge({ className, children }) {
  return (
    <span className={`rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${className}`}>
      {children}
    </span>
  );
}
